/**
 * Unit conversion utilities for ingredient and recipe calculations
 * 
 * Provides comprehensive unit conversion tables and functions for:
 * - Weight (lb, oz, kg, g)
 * - Volume (gal, qt, pt, liter, ml, cup, fl oz, tbsp, tsp)
 * - Count (each, piece, dozen, case, box, pack, bag, can, bunch, head, bottle, jar)
 */

/**
 * Comprehensive unit conversion table
 * Maps from unit -> to unit -> conversion factor
 */
export const UNIT_CONVERSIONS: Record<string, Record<string, number>> = {
  // Weight conversions
  'lb': { 'oz': 16, 'g': 453.592, 'kg': 0.453592, 'lb': 1 },
  'oz': { 'oz': 1, 'g': 28.3495, 'kg': 0.0283495, 'lb': 0.0625 },
  'kg': { 'oz': 35.274, 'g': 1000, 'kg': 1, 'lb': 2.20462 },
  'g': { 'oz': 0.035274, 'g': 1, 'kg': 0.001, 'lb': 0.00220462 },
  
  // Volume conversions
  'gallon': { 'liter': 3.78541, 'ml': 3785.41, 'cup': 16, 'tbsp': 256, 'tsp': 768, 'quart': 4, 'pt': 8, 'oz': 128, 'fl oz': 128, 'gallon': 1 },
  'gal': { 'liter': 3.78541, 'ml': 3785.41, 'cup': 16, 'tbsp': 256, 'tsp': 768, 'quart': 4, 'qt': 4, 'pt': 8, 'oz': 128, 'fl oz': 128, 'gallon': 1, 'gal': 1 },
  'liter': { 'liter': 1, 'ml': 1000, 'cup': 4.22675, 'tbsp': 67.628, 'tsp': 202.884, 'quart': 1.05669, 'qt': 1.05669, 'pt': 2.11338, 'gallon': 0.264172, 'gal': 0.264172, 'oz': 33.814, 'fl oz': 33.814 },
  'qt': { 'liter': 0.946353, 'ml': 946.353, 'cup': 4, 'tbsp': 64, 'tsp': 192, 'quart': 1, 'pt': 2, 'gallon': 0.25, 'gal': 0.25, 'oz': 32, 'fl oz': 32 },
  'quart': { 'liter': 0.946353, 'ml': 946.353, 'cup': 4, 'tbsp': 64, 'tsp': 192, 'quart': 1, 'qt': 1, 'pt': 2, 'gallon': 0.25, 'gal': 0.25, 'oz': 32, 'fl oz': 32 },
  'pt': { 'liter': 0.473176, 'ml': 473.176, 'cup': 2, 'tbsp': 32, 'tsp': 96, 'quart': 0.5, 'qt': 0.5, 'gallon': 0.125, 'gal': 0.125, 'oz': 16, 'fl oz': 16 },
  'cup': { 'liter': 0.236588, 'ml': 236.588, 'cup': 1, 'tbsp': 16, 'tsp': 48, 'quart': 0.25, 'qt': 0.25, 'pt': 0.5, 'gallon': 0.0625, 'gal': 0.0625, 'oz': 8, 'fl oz': 8 },
  'fl oz': { 'liter': 0.0295735, 'ml': 29.5735, 'cup': 0.125, 'tbsp': 2, 'tsp': 6, 'quart': 0.03125, 'qt': 0.03125, 'pt': 0.0625, 'gallon': 0.0078125, 'gal': 0.0078125, 'oz': 1, 'fl oz': 1 },
  'tbsp': { 'liter': 0.0147868, 'ml': 14.7868, 'cup': 0.0625, 'tbsp': 1, 'tsp': 3, 'quart': 0.015625, 'qt': 0.015625, 'pt': 0.03125, 'gallon': 0.00390625, 'gal': 0.00390625, 'oz': 0.5, 'fl oz': 0.5 },
  'tsp': { 'liter': 0.00492892, 'ml': 4.92892, 'cup': 0.0208333, 'tbsp': 0.333333, 'tsp': 1, 'quart': 0.00520833, 'qt': 0.00520833, 'pt': 0.0104167, 'gallon': 0.00130208, 'gal': 0.00130208, 'oz': 0.166667, 'fl oz': 0.166667 },
  'ml': { 'liter': 0.001, 'ml': 1, 'cup': 0.00422675, 'tbsp': 0.067628, 'tsp': 0.202884, 'quart': 0.00105669, 'qt': 0.00105669, 'pt': 0.00211338, 'gallon': 0.000264172, 'gal': 0.000264172, 'oz': 0.033814, 'fl oz': 0.033814 },
  
  // Count/piece conversions
  'each': { 'each': 1, 'piece': 1, 'slice': 1, 'dozen': 0.0833333 },
  'piece': { 'each': 1, 'piece': 1, 'slice': 1, 'dozen': 0.0833333 },
  'slice': { 'each': 1, 'piece': 1, 'slice': 1, 'dozen': 0.0833333 },
  'dozen': { 'each': 12, 'piece': 12, 'slice': 12, 'dozen': 1 },
  
  // Length conversions
  'feet': { 'feet': 1, 'inches': 12 },
  'inches': { 'feet': 0.0833333, 'inches': 1 },
  
  // Case/bulk conversions (common restaurant sizes)
  'case': { 'each': 24, 'piece': 24, 'can': 24, 'bottle': 24 },
  'box': { 'each': 12, 'piece': 12 },
  'bag': { 'lb': 50, 'kg': 22.68 },
  'pack': { 'each': 1, 'piece': 1 },
  'bunch': { 'each': 1, 'piece': 1 },
  'head': { 'each': 1, 'piece': 1 },
  'can': { 'each': 1, 'piece': 1 },
  'bottle': { 'each': 1, 'piece': 1 },
  'jar': { 'each': 1, 'piece': 1 },
  'pallet': { 'case': 40, 'box': 80 },
};

/**
 * Convert a value from one unit to another
 * @param value - The numeric value to convert
 * @param fromUnit - The source unit
 * @param toUnit - The target unit
 * @returns The converted value, or the original value if no conversion is available
 */
export const convertUnits = (value: number, fromUnit: string, toUnit: string): number => {
  if (!fromUnit || !toUnit) return value;
  if (fromUnit === toUnit) return value;
  
  const from = fromUnit.toLowerCase().trim();
  const to = toUnit.toLowerCase().trim();
  
  // Direct conversion lookup
  if (UNIT_CONVERSIONS[from]?.[to]) {
    return value * UNIT_CONVERSIONS[from][to];
  }
  
  // Try inverse conversion
  if (UNIT_CONVERSIONS[to]?.[from]) {
    return value / UNIT_CONVERSIONS[to][from];
  }
  
  // If no conversion found, return original value (user will need to specify manually)
  return value;
};

/**
 * Get conversion factor between two units
 * @param fromUnit - The source unit
 * @param toUnit - The target unit
 * @returns The conversion factor (multiply source value by this to get target value)
 */
export const getConversionFactor = (fromUnit: string, toUnit: string): number => {
  if (!fromUnit || !toUnit) return 1;
  if (fromUnit === toUnit) return 1;
  
  const from = fromUnit.toLowerCase().trim();
  const to = toUnit.toLowerCase().trim();
  
  // Direct conversion lookup
  if (UNIT_CONVERSIONS[from]?.[to]) {
    return UNIT_CONVERSIONS[from][to];
  }
  
  // Try inverse conversion
  if (UNIT_CONVERSIONS[to]?.[from]) {
    return 1 / UNIT_CONVERSIONS[to][from];
  }
  
  // Default fallbacks for common cases
  if (from === 'case' && to === 'each') return 24;
  if (from === 'dozen' && to === 'each') return 12;
  if (from === 'lb' && to === 'oz') return 16;
  if (from === 'gallon' && to === 'cup') return 16;
  if (from === 'gal' && to === 'cup') return 16;
  
  // If no conversion found, return 1 (user will need to specify manually)
  return 1;
};

/**
 * Deduplicated, comprehensive unit list for dropdowns
 * Organized by category for better UX
 */
export const STANDARD_UNIT_OPTIONS = [
  // Weight
  'lb', 'oz', 'kg', 'g',
  // Volume
  'gal', 'qt', 'pt', 'liter', 'ml', 'cup', 'fl oz', 'tbsp', 'tsp',
  // Count
  'each', 'piece', 'dozen', 'case', 'box', 'pack', 'bag', 'can',
  'bunch', 'head', 'bottle', 'jar',
];
